import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyOrders = () => {
  const [email, setEmail] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setSearched(true);

    try {
      const response = await axios.get(`${API}/orders/email/${email}`);
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="my-orders-page py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold text-gray-900 mb-8 text-center">
          My Orders
        </h1>

        {/* Search Form */}
        <Card className="mb-12 max-w-2xl mx-auto">
          <CardContent className="p-8">
            <form onSubmit={handleSearch} className="flex gap-4">
              <Input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1"
              />
              <Button type="submit" disabled={loading}>
                <Search className="h-5 w-5 mr-2" />
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Orders List */}
        {searched && (
          <div>
            {orders.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-xl text-gray-600">No orders found for this email.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {orders.map((order) => (
                  <Card key={order._id} className="hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="p-8">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-2">
                            Order #{order.order_number}
                          </h3>
                          <p className="text-gray-600">
                            {new Date(order.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex gap-3 mt-4 md:mt-0">
                          <span className={`px-4 py-2 rounded-full font-semibold text-sm ${
                            order.status === 'delivered'
                              ? 'bg-green-100 text-green-700'
                              : order.status === 'shipped'
                              ? 'bg-blue-100 text-blue-700'
                              : order.status === 'cancelled'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                          </span>
                          <span className={`px-4 py-2 rounded-full font-semibold text-sm ${
                            order.payment_status === 'completed'
                              ? 'bg-green-100 text-green-700'
                              : order.payment_status === 'failed'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {order.payment_status.charAt(0).toUpperCase() + order.payment_status.slice(1)}
                          </span>
                        </div>
                      </div>

                      <div className="border-t pt-6">
                        <h4 className="font-bold text-gray-900 mb-4">Items:</h4>
                        <div className="space-y-2 mb-4">
                          {order.items.map((item, index) => (
                            <div key={item.product_id || item._id || `mo-${order._id}-${index}`} className="flex justify-between text-gray-700">
                              <span>{item.product_name} x {item.quantity}</span>
                              <span className="font-semibold">
                                ₦{item.total_price.toLocaleString()}
                              </span>
                            </div>
                          ))}
                        </div>

                        <div className="flex justify-between items-center pt-4 border-t">
                          <span className="text-lg font-bold text-gray-900">Total:</span>
                          <span className="text-2xl font-bold text-green-600">
                            ₦{order.total_amount.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyOrders;